import { FC, useState } from 'react';
import {
    Upload,
    Button,
    Typography,
    Space,
    Alert,
} from 'antd';
import { FiUploadCloud, FiDownload } from 'react-icons/fi';
import type { UploadFile } from 'antd/es/upload/interface';

import { BulkColumnConfig, TranslateFn, ValidatedRow } from './types';
import {
    buildColumnKeyMap,
    downloadTemplate,
    mapRow,
    parseFile,
} from './utils';

const { Text, Title } = Typography;

interface FileUploadStepProps {
    columns: BulkColumnConfig[];
    templateFileName: string;
    validate: (rows: Record<string, any>[]) => ValidatedRow[];
    onFileProcessed: (rows: ValidatedRow[]) => void;
    t: TranslateFn;
}

const FileUploadStep: FC<FileUploadStepProps> = ({
    columns, templateFileName, validate, onFileProcessed, t,
}) => {
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const columnKeyMap = buildColumnKeyMap(columns);

    const handleUpload = async (file: File) => {
        setLoading(true);
        setError(null);

        try {
            const rawRows = await parseFile(file);
            const mappedRows = rawRows.map((row) => mapRow(row, columnKeyMap));
            const validated = validate(mappedRows);
            onFileProcessed(validated);
        } catch (err: any) {
            const errorKey = err.message === 'EMPTY_FILE'
                ? 'BULK.ERROR_EMPTY_FILE'
                : 'BULK.ERROR_INVALID_FILE';
            setError(t(errorKey));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <Title level={5} style={{ marginBottom: 8 }}>
                    {t('BULK.UPLOAD_TITLE')}
                </Title>
                <Text type="secondary">
                    {t('BULK.UPLOAD_DESCRIPTION')}
                </Text>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <Button
                    icon={<FiDownload style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                    onClick={() => downloadTemplate(columns, templateFileName)}
                >
                    {t('BULK.DOWNLOAD_TEMPLATE')}
                </Button>
            </div>

            {error && (
                <Alert
                    message={error}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setError(null)}
                />
            )}

            <Upload.Dragger
                accept=".csv,.xlsx,.xls"
                fileList={fileList}
                maxCount={1}
                beforeUpload={(file) => {
                    setFileList([file as unknown as UploadFile]);
                    handleUpload(file);
                    return false;
                }}
                onRemove={() => {
                    setFileList([]);
                    setError(null);
                }}
                disabled={loading}
            >
                <p style={{ fontSize: 40, color: 'var(--color-primary)', marginBottom: 8 }}>
                    <FiUploadCloud />
                </p>
                <p style={{ fontWeight: 500 }}>
                    {loading ? t('BULK.PROCESSING') : t('BULK.DRAG_FILE')}
                </p>
                <p style={{ color: 'var(--color-dark-3)', fontSize: 12 }}>
                    {t('BULK.SUPPORTED_FORMATS')}
                </p>
            </Upload.Dragger>

            <Alert
                message={t('BULK.INSTRUCTIONS_TITLE')}
                description={(
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                        <li>{t('BULK.INSTRUCTION_1')}</li>
                        <li>{t('BULK.INSTRUCTION_2')}</li>
                        <li>{t('BULK.INSTRUCTION_3')}</li>
                        <li>{t('BULK.INSTRUCTION_4')}</li>
                    </ol>
                )}
                type="info"
                showIcon
            />
        </Space>
    );
};

export { FileUploadStep };
